import { useEffect, useState } from 'react';
import {
  criteriasList,
  getClients,
  getCountries,
  getPostalStructures,
  getProviders,
  getServiceCodes,
  getServiceTypes,
  getZones,
} from '../../services/rulesService';
import { CriteriaList } from '../../interfaces/rule';
import { isGeolocation, isCountry } from '../../utils/Rules/CriteriaValidator';
import { AxiosCountryRepository } from '../../company/infrastructure/AxiosCountryRepository';
import { ListCountryUseCase } from '../../company/usecases/ListCountryUseCase';
import { adaptCriteriaList } from '../../utils/Rules/CriteriaLabelAdapter';

interface CountryOption {
  label: string;
  value: string;
}

let countriesCache: CountryOption[] | null = null;
let countriesPromise: Promise<CountryOption[]> | null = null;

export const clearCountriesCache = () => {
  countriesCache = null;
  countriesPromise = null;
};

const fetchCountriesMemo = async (): Promise<CountryOption[]> => {
  if (countriesCache) return countriesCache;

  if (!countriesPromise) {
    countriesPromise = getCountries()
      .then(response => {
        const mapped = response.data.map((c: { name: string; code: string }) => ({ label: c.name, value: c.code }));
        countriesCache = mapped;
        return mapped;
      })
      .catch(error => {
        countriesPromise = null;
        throw error;
      });
  }

  return countriesPromise;
};

export const useCriteria = () => {
  const [criterias, setCriterias] = useState<CriteriaList[]>([]);
  const [fetchApiList, setFetchApiList] = useState<string[]>([]);
  const [loadingCities, setLoadingCities] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      const response = await criteriasList();
      setCriterias(adaptCriteriaList(response.data));
    };
    fetchData();
  }, []);

  const fetchCountries = async () => {
    try {
      return await fetchCountriesMemo();
    } catch (error) {
      console.error(error);
    }
  };

  const fetchServiceCode = async (criteriaKey: string) => {

    let dataPromise;
    switch (criteriaKey) {
      case 'service_code':
        dataPromise = getServiceCodes();
        break;
      case 'service_types':
        dataPromise = getServiceTypes();
        break;
      case 'clients':
        dataPromise = getClients();
        break;
      case 'distribution_provider':
        dataPromise = getProviders();
        break;
      default:
        dataPromise = null;
    }

    const data = await dataPromise;

    if (data && data.data) {
      return data.data
        .filter(
          (obj: { name: string; code: string }) =>
            obj.name !== null,
        )
        .map((obj: { name: string; code: string }) => ({
          label: `${obj.name} - ${obj.code}`,
          value: obj.code,
        }));
    }
  };

  const loadCountriesAndCities = () => {
    if (!fetchApiList.includes('countries')) {
      fetchCountries().then(response => {
        setCriterias(prev => prev.map(c => {
          if (isGeolocation(c.key)) {
            return { ...c, countries: response };
          }
          return c;
        }));
        setFetchApiList([...fetchApiList, 'countries']);
      });
    }
  };

  const loadCountriesForCountryCriteria = () => {
    if (!fetchApiList.includes('country')) {
      const repository = new AxiosCountryRepository();
      const useCase = new ListCountryUseCase(repository);
      useCase.execute()
        .then(countries => {
          const mapped = countries.map(c => ({ label: c.name, value: c.code }));
          setCriterias(prev => prev.map(c => {
            if (isCountry(c.key)) {
              return { ...c, countriesList: mapped };
            }
            return c;
          }));
          setFetchApiList(prev => [...prev, 'country']);
        })
        .catch(error => {
          console.error('[useCriteria] Error loading countries:', error);
        });
    }
  };

  const loadCities = (criteriaKey: string, countryCode: string, level: number) => {
    setLoadingCities(criteriaKey);
    getPostalStructures(countryCode, level).then((response) => {
      setCriterias(prev => prev.map(c => {
        if (criteriaKey == c.key) {
          return {
            ...c,
            cities: response.data.map((c: { name: string }) => ({ label: c.name, value: c.name })),
          };
        }
        return c;
      }));
    }).finally(() => {
      setLoadingCities(null);
    });
  };

  const loadServiceCode = (criteriaKey: string) => {
    if (!fetchApiList.includes(criteriaKey)) {
      fetchServiceCode(criteriaKey).then(response => {
        setCriterias(prev => prev.map(c => {
          if (criteriaKey === c.key) {
            return { ...c, serviceCode: response };
          }
          return c;
        }));
        setFetchApiList([...fetchApiList, criteriaKey]);
      });
    }
  };

  const fetchZones = async () => {
    try {
      const data = await getZones();

      if (data && data.data) {
        return (data.data as { name: string; key: string }[])
          .filter((obj) => obj.name !== null)
          .map((obj) => ({
            label: obj.name,
            value: obj.key,
          }));
      }
      return [];
    } catch (error) {
      console.error('[useCriteria] Error loading zones:', error);
      return [];
    }
  };

  const loadZones = (criteriaKey: string) => {
    if (!fetchApiList.includes(criteriaKey)) {
      fetchZones().then(response => {
        setCriterias(prev => prev.map(c => {
          if (criteriaKey === c.key) {
            return { ...c, zones: response };
          }
          return c;
        }));
        setFetchApiList([...fetchApiList, criteriaKey]);
      });
    }
  };


  return {
    criterias,
    loadingCities,
    loadCountriesAndCities,
    loadCountriesForCountryCriteria,
    loadServiceCode,
    fetchServiceCode,
    loadCities,
    loadZones,
    fetchZones,
  };
};
